import { clear } from "./dom";
import { signal } from "./signals";

/** A page renderer draws into the outlet and may return a cleanup function. */
export type PageRenderer = (outlet: HTMLElement) => void | (() => void);

function parsePath(): string {
  const hash = location.hash.replace(/^#/, "");
  return hash || "/";
}

/** Current route path (e.g. "/bloom"), as a reactive signal for nav highlighting. */
export const currentPath = signal(parsePath());

export function navigate(path: string): void {
  if (parsePath() === path) {
    window.scrollTo({ top: 0 });
    return;
  }
  location.hash = path;
}

export function startRouter(
  outlet: HTMLElement,
  routes: Record<string, PageRenderer>,
  notFound: PageRenderer,
): void {
  let dispose: (() => void) | void;

  function handle(): void {
    const path = parsePath();
    currentPath.value = path;
    if (dispose) dispose();
    clear(outlet);
    const renderer = routes[path] ?? notFound;
    dispose = renderer(outlet) || undefined;

    // Reflect the current page in the document title (tabs, history, SEO).
    const heading = outlet.querySelector("h1")?.textContent?.trim();
    document.title =
      path === "/" || !heading
        ? "Probably — an interactive lab for probabilistic data structures"
        : `${heading} · Probably`;

    window.scrollTo({ top: 0 });
  }

  window.addEventListener("hashchange", handle);
  handle();
}
